/**
 * The atmosphere, as physics rather than as a gradient.
 *
 * CLAUDE.md calls the sky and its aerial perspective "the #1 realism seller", and the reason is
 * worth stating because it decides the architecture. Everything the eye uses to judge distance
 * outdoors past a kilometre — how blue a ridge is, how washed out, how much brighter the sky is
 * near the horizon than overhead, why a sunset is red at the horizon and still blue at the zenith
 * — comes from *one* physical process: light scattering off air molecules and aerosols on its way
 * to you. Model that process once, properly, and the sky, the haze on the mountains, the colour
 * of the sun and the light falling on the terrain all come out consistent with each other for
 * free. Fake them separately and they disagree, and the eye notices the disagreement long before
 * it notices any individual error.
 *
 * So this file is deliberately **pure maths with no Three.js in it**. It is the single source of
 * truth for atmospheric radiance, and it is a plain module because:
 *
 *   - it can be unit-tested against published values (see `model.test.ts`) — a shader cannot;
 *   - it runs on the CPU to bake small lookup tables, so the per-pixel GPU cost of the sky
 *     collapses to one texture fetch instead of a raymarch (see `skyView.ts`);
 *   - the same numbers drive the aerial-perspective fog node, the sun's colour and intensity,
 *     the ambient fill, and the auto-exposure target. One model, four consumers, no drift.
 *
 * ## The model
 *
 * Standard atmospheric-scattering practice, in the lineage of Bruneton & Neyret 2008 and
 * Hillaire 2020 ("A Scalable and Production Ready Sky and Atmosphere Rendering Technique"):
 *
 *   - **Rayleigh scattering** off air molecules. Cross-section goes as 1/λ⁴, which is why the
 *     blue channel scatters ~5.7× more strongly than red — the whole reason the sky is blue and
 *     the setting sun is not. Density falls exponentially with a scale height of 8 km.
 *   - **Mie scattering** off aerosols (dust, water droplets, pollution). Nearly wavelength
 *     independent — this is the white haze near the horizon and the bright halo around the sun.
 *     Scale height 1.2 km, so it hugs the ground, which is why the murk sits in the valleys.
 *   - **Ozone absorption**, a layer around 25 km that absorbs in the Chappuis band. It does
 *     almost nothing at noon and it is *the* reason a clear zenith stays deep blue at twilight
 *     instead of going grey: by then the sunlight reaching you has taken a long horizontal path
 *     through the ozone layer, which eats the yellows and greens. Skip it and dusk looks wrong
 *     in a way that is hard to name but obvious side by side.
 *   - **Multiple scattering**, via Hillaire's isotropic approximation. Single scattering alone
 *     gives a sky that is far too dark near the horizon and shadowed ground that is nearly
 *     black, because in reality most of the light reaching you from a bright sky has bounced
 *     more than once.
 *
 * All coefficients are **per metre**, all distances **metres**, all angles **radians**, and all
 * radiances are relative to a solar irradiance of 1 — the absolute scale is applied once, at the
 * renderer boundary, by the exposure model.
 */

/** A radiance or coefficient triple. Mutable and passed as an `out` parameter — see below. */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function rgb(r = 0, g = 0, b = 0): Rgb {
  return { r, g, b };
}

export function setRgb(out: Rgb, r: number, g: number, b: number): Rgb {
  out.r = r;
  out.g = g;
  out.b = b;
  return out;
}

export function copyRgb(out: Rgb, source: Rgb): Rgb {
  out.r = source.r;
  out.g = source.g;
  out.b = source.b;
  return out;
}

/**
 * Physical description of a planet's atmosphere.
 *
 * Data driven rather than hard-coded constants so the tests can run degenerate atmospheres
 * (no ozone, no Mie) and check that each term does what it is claimed to do in isolation.
 */
export interface AtmosphereParams {
  /** Planet radius [m]. Not the WGS84 value: the standard scattering radius, see below. */
  groundRadiusM: number;
  /** Radius of the top of the atmosphere [m]. */
  topRadiusM: number;

  /** Rayleigh scattering coefficient at sea level, per metre, per channel. */
  rayleighScattering: Rgb;
  /** Rayleigh density scale height [m]. */
  rayleighScaleHeightM: number;

  /** Mie scattering coefficient at sea level [1/m]; wavelength independent to a good approximation. */
  mieScattering: number;
  /** Mie *extinction* at sea level [1/m] — scattering plus absorption by the aerosol itself. */
  mieExtinction: number;
  /** Mie density scale height [m]. */
  mieScaleHeightM: number;
  /** Mie phase asymmetry, 0…1. Higher means more forward-scattering (a tighter sun halo). */
  mieAnisotropy: number;

  /** Peak ozone absorption coefficient, per metre, per channel. */
  ozoneAbsorption: Rgb;
  /** Altitude of the ozone layer's peak [m]. */
  ozoneCentreM: number;
  /** Half-width of the (tent-shaped) ozone layer [m]. */
  ozoneWidthM: number;

  /** Average ground albedo, used by the multiple-scattering term. */
  groundAlbedo: Rgb;
}

/**
 * Earth.
 *
 * The radius is 6360 km rather than the WGS84 6378 km, and that is not sloppiness — it is the
 * value the reference implementations of this model are tuned against, and the atmosphere
 * thickness (100 km) is quantised to match. An 18 km discrepancy in planet radius changes the
 * horizon's scattering path length by well under a percent; using the literature's numbers means
 * the coefficients below can be taken from the literature too, and checked against it. The
 * *geometry* of the world — where the horizon is, where the terrain sits — uses the real WGS84
 * ellipsoid throughout (see world/geodesy.ts). These two radii never meet.
 */
export const EARTH_ATMOSPHERE: Readonly<AtmosphereParams> = Object.freeze({
  groundRadiusM: 6_360_000,
  topRadiusM: 6_460_000,

  // λ = 680 / 550 / 440 nm. The 1/λ⁴ signature is visible in the ratios: blue is 5.7× red.
  rayleighScattering: Object.freeze(rgb(5.802e-6, 13.558e-6, 33.1e-6)) as Rgb,
  rayleighScaleHeightM: 8_000,

  mieScattering: 3.996e-6,
  mieExtinction: 4.44e-6,
  mieScaleHeightM: 1_200,
  mieAnisotropy: 0.8,

  ozoneAbsorption: Object.freeze(rgb(0.65e-6, 1.881e-6, 0.085e-6)) as Rgb,
  ozoneCentreM: 25_000,
  ozoneWidthM: 15_000,

  groundAlbedo: Object.freeze(rgb(0.3, 0.3, 0.3)) as Rgb,
});

// --- densities -----------------------------------------------------------------------------

/** Rayleigh density relative to sea level at altitude `heightM`. */
export function rayleighDensity(params: AtmosphereParams, heightM: number): number {
  return Math.exp(-Math.max(0, heightM) / params.rayleighScaleHeightM);
}

/** Mie density relative to sea level at altitude `heightM`. */
export function mieDensity(params: AtmosphereParams, heightM: number): number {
  return Math.exp(-Math.max(0, heightM) / params.mieScaleHeightM);
}

/**
 * Ozone density relative to its peak, as the usual symmetric tent.
 *
 * A tent rather than an exponential because ozone is a *layer*, not a well-mixed gas: it is
 * produced by UV photolysis at altitude and destroyed near the ground, so its profile has a
 * maximum in the middle of the stratosphere and falls away on both sides.
 */
export function ozoneDensity(params: AtmosphereParams, heightM: number): number {
  return Math.max(0, 1 - Math.abs(heightM - params.ozoneCentreM) / params.ozoneWidthM);
}

/** Total extinction (scattering + absorption) at an altitude, per metre, per channel. */
export function extinctionAt(params: AtmosphereParams, heightM: number, out: Rgb): Rgb {
  const dR = rayleighDensity(params, heightM);
  const dM = mieDensity(params, heightM);
  const dO = ozoneDensity(params, heightM);
  const mie = params.mieExtinction * dM;
  out.r = params.rayleighScattering.r * dR + mie + params.ozoneAbsorption.r * dO;
  out.g = params.rayleighScattering.g * dR + mie + params.ozoneAbsorption.g * dO;
  out.b = params.rayleighScattering.b * dR + mie + params.ozoneAbsorption.b * dO;
  return out;
}

/** Rayleigh scattering coefficient at an altitude, per metre, per channel. */
export function rayleighScatteringAt(params: AtmosphereParams, heightM: number, out: Rgb): Rgb {
  const d = rayleighDensity(params, heightM);
  out.r = params.rayleighScattering.r * d;
  out.g = params.rayleighScattering.g * d;
  out.b = params.rayleighScattering.b * d;
  return out;
}

/** Mie scattering coefficient at an altitude [1/m]. Grey, so a scalar. */
export function mieScatteringAt(params: AtmosphereParams, heightM: number): number {
  return params.mieScattering * mieDensity(params, heightM);
}

// --- phase functions -----------------------------------------------------------------------

/**
 * Rayleigh phase function, normalised so it integrates to 1 over the sphere.
 *
 * `3/(16π)·(1 + cos²θ)`. Symmetric forward and back, with a factor-of-two dip at 90° — which
 * is why the sky is measurably darker (and strongly polarised) at a right angle to the sun.
 */
export function rayleighPhase(cosTheta: number): number {
  return (3 / (16 * Math.PI)) * (1 + cosTheta * cosTheta);
}

/**
 * Cornette–Shanks phase function for Mie scattering.
 *
 * The Henyey–Greenstein function is the usual cheap choice, but it is unnormalised and its
 * backscatter is visibly wrong. Cornette–Shanks costs one extra multiply, is properly
 * normalised, and reduces to Rayleigh's shape as `g → 0` — which makes it the honest choice
 * when the same code path has to cover both hazy and clean air.
 */
export function miePhase(cosTheta: number, g: number): number {
  const g2 = g * g;
  const numerator = 3 * (1 - g2) * (1 + cosTheta * cosTheta);
  const denominator = 8 * Math.PI * (2 + g2) * Math.pow(1 + g2 - 2 * g * cosTheta, 1.5);
  return numerator / denominator;
}

// --- ray geometry ---------------------------------------------------------------------------

/**
 * Distance from a point at radius `r` travelling with zenith cosine `mu` to a sphere of radius
 * `radius`, or −1 when the ray misses.
 *
 * `mu` is the cosine of the angle between the ray and the local zenith (straight up), so
 * `mu = 1` is straight up and `mu = −1` is straight down. This is the standard parameterisation
 * for a spherically symmetric atmosphere: everything depends only on (radius, zenith cosine),
 * never on longitude or on which way is north, which is what makes a 2D lookup table sufficient
 * for a 3D problem.
 */
export function raySphereIntersect(r: number, mu: number, radius: number): number {
  const discriminant = r * r * (mu * mu - 1) + radius * radius;
  if (discriminant < 0) return -1;
  const root = Math.sqrt(discriminant);
  // The near root first; if it is behind us, the far one.
  const near = -r * mu - root;
  const far = -r * mu + root;
  if (far < 0) return -1;
  return near >= 0 ? near : far;
}

/** Does a ray from radius `r` with zenith cosine `mu` hit the ground? */
export function hitsGround(params: AtmosphereParams, r: number, mu: number): boolean {
  return mu < 0 && r * r * (mu * mu - 1) + params.groundRadiusM * params.groundRadiusM >= 0;
}

/**
 * Distance to where the ray leaves the atmosphere — the ground if it hits it, the top of the
 * atmosphere otherwise. This is the integration limit for every march below.
 */
export function distanceToAtmosphereExit(
  params: AtmosphereParams,
  r: number,
  mu: number,
): number {
  if (hitsGround(params, r, mu)) {
    const ground = raySphereIntersect(r, mu, params.groundRadiusM);
    if (ground >= 0) return ground;
  }
  return Math.max(0, raySphereIntersect(r, mu, params.topRadiusM));
}

// --- transmittance ---------------------------------------------------------------------------

const scratchExtinction = /* #__PURE__ */ rgb();

/**
 * Transmittance from a point to the top of the atmosphere along a ray, per channel.
 *
 * Ray-marched rather than analytic: an exponential atmosphere has no closed-form Chapman
 * function that also covers the ozone tent, and this runs at bake time rather than per pixel, so
 * accuracy is worth more than speed. 64 steps with the midpoint rule stays within 1.1e−3
 * absolute of an 8000-step reference everywhere the model is used — worst case just above the
 * horizon, where the path is 1000 km long and the density profile along it varies fastest. The
 * tests assert the bound rather than trusting the comment.
 *
 * Returns `out` set to zero when the ray hits the ground — nothing gets through a planet.
 */
export function transmittanceToTop(
  params: AtmosphereParams,
  r: number,
  mu: number,
  out: Rgb,
  steps = 64,
): Rgb {
  if (hitsGround(params, r, mu)) return setRgb(out, 0, 0, 0);

  const distance = raySphereIntersect(r, mu, params.topRadiusM);
  if (!(distance > 0)) return setRgb(out, 1, 1, 1);

  const dt = distance / steps;
  let opticalDepthR = 0;
  let opticalDepthG = 0;
  let opticalDepthB = 0;

  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) * dt;
    // Law of cosines: the radius at distance t along a ray leaving radius r at zenith cosine mu.
    const radius = Math.sqrt(t * t + 2 * r * mu * t + r * r);
    extinctionAt(params, radius - params.groundRadiusM, scratchExtinction);
    opticalDepthR += scratchExtinction.r * dt;
    opticalDepthG += scratchExtinction.g * dt;
    opticalDepthB += scratchExtinction.b * dt;
  }

  return setRgb(
    out,
    Math.exp(-opticalDepthR),
    Math.exp(-opticalDepthG),
    Math.exp(-opticalDepthB),
  );
}

/**
 * A baked transmittance table, so the scattering integrals below do not re-march it per sample.
 *
 * Parameterised on (altitude, zenith cosine) with a **square-root mapping on `mu`**. A linear
 * mapping wastes almost the whole table on directions well above the horizon, where
 * transmittance is nearly 1 and nearly constant, and resolves the few degrees either side of the
 * horizon — where it falls off a cliff and where every sunset colour lives — with two or three
 * texels. The square root spends the resolution where the function actually varies.
 */
export class TransmittanceTable {
  readonly widthMu: number;
  readonly heightAltitude: number;
  /** RGB triples, row-major, `heightAltitude` rows of `widthMu`. */
  readonly data: Float32Array;

  private readonly params: AtmosphereParams;

  constructor(params: AtmosphereParams = EARTH_ATMOSPHERE, widthMu = 128, heightAltitude = 48) {
    this.params = params;
    this.widthMu = widthMu;
    this.heightAltitude = heightAltitude;
    this.data = new Float32Array(widthMu * heightAltitude * 3);
    this.build();
  }

  private build(): void {
    const { params } = this;
    const thicknessM = params.topRadiusM - params.groundRadiusM;
    const sample = rgb();

    for (let y = 0; y < this.heightAltitude; y++) {
      // Altitudes are also non-linear: the bottom 10 km carries essentially all the air.
      const v = y / (this.heightAltitude - 1);
      const altitude = v * v * thicknessM;
      const r = params.groundRadiusM + altitude;

      for (let x = 0; x < this.widthMu; x++) {
        const u = x / (this.widthMu - 1);
        const mu = muFromTableCoordinate(u);
        transmittanceToTop(params, r, mu, sample, 64);
        const index = (y * this.widthMu + x) * 3;
        this.data[index] = sample.r;
        this.data[index + 1] = sample.g;
        this.data[index + 2] = sample.b;
      }
    }
  }

  /** Bilinear lookup. `heightM` is altitude above the ground, `mu` the zenith cosine. */
  sample(heightM: number, mu: number, out: Rgb): Rgb {
    const thicknessM = this.params.topRadiusM - this.params.groundRadiusM;
    const altitude = Math.min(Math.max(heightM, 0), thicknessM);
    const v = Math.sqrt(altitude / thicknessM) * (this.heightAltitude - 1);
    const u = tableCoordinateFromMu(mu) * (this.widthMu - 1);

    const x0 = Math.min(this.widthMu - 1, Math.max(0, Math.floor(u)));
    const x1 = Math.min(this.widthMu - 1, x0 + 1);
    const y0 = Math.min(this.heightAltitude - 1, Math.max(0, Math.floor(v)));
    const y1 = Math.min(this.heightAltitude - 1, y0 + 1);
    const fx = u - x0;
    const fy = v - y0;

    const i00 = (y0 * this.widthMu + x0) * 3;
    const i10 = (y0 * this.widthMu + x1) * 3;
    const i01 = (y1 * this.widthMu + x0) * 3;
    const i11 = (y1 * this.widthMu + x1) * 3;

    for (let c = 0; c < 3; c++) {
      const top = this.data[i00 + c] * (1 - fx) + this.data[i10 + c] * fx;
      const bottom = this.data[i01 + c] * (1 - fx) + this.data[i11 + c] * fx;
      const value = top * (1 - fy) + bottom * fy;
      if (c === 0) out.r = value;
      else if (c === 1) out.g = value;
      else out.b = value;
    }
    return out;
  }
}

/**
 * Table coordinate ↔ zenith cosine, with the resolution concentrated near the horizon.
 *
 * `mu ∈ [−1, 1]` maps to `u ∈ [0, 1]`; the square root on the upper half puts roughly a third of
 * the texels in the top 10° above the horizon.
 */
export function tableCoordinateFromMu(mu: number): number {
  const clamped = Math.min(1, Math.max(-1, mu));
  return clamped >= 0
    ? 0.5 + 0.5 * Math.sqrt(clamped)
    : 0.5 - 0.5 * Math.sqrt(-clamped);
}

export function muFromTableCoordinate(u: number): number {
  const centred = (Math.min(1, Math.max(0, u)) - 0.5) * 2;
  return centred >= 0 ? centred * centred : -(centred * centred);
}

// --- multiple scattering ----------------------------------------------------------------------

const msScratchA = /* #__PURE__ */ rgb();
const msScratchB = /* #__PURE__ */ rgb();

/**
 * Hillaire's isotropic multiple-scattering table.
 *
 * ### Why it exists
 *
 * Single scattering — one bounce from the sun into your eye — accounts for less than half the
 * light coming from a clear daytime sky, and much less than that near the horizon or under an
 * overcast. Render single scattering alone and you get the classic too-dark-too-saturated sky:
 * a horizon that goes navy instead of pale, terrain in shadow that is nearly black, and a
 * twilight that snaps to night the moment the sun crosses the horizon.
 *
 * ### Why one table suffices
 *
 * The trick is that after the first couple of bounces the light field is very nearly isotropic —
 * it has forgotten which way the sun was. So instead of tracking direction, integrate the
 * *total* second-order scattered energy arriving at a point from all directions, then sum the
 * geometric series `1 + f + f² + …  = 1/(1 − f)` for all further orders. That is one scalar
 * transfer factor per (altitude, sun zenith), which is a 2D table — and, crucially, one that
 * does not depend on the view direction, so it is built **once at start-up** and never rebuilt.
 *
 * Cost: `resolution² × directions × steps` inner iterations. At the shipped 32×32×8×20 that is
 * about 160 k steps, a few tens of milliseconds during the boot splash, and nothing thereafter.
 */
export class MultipleScatteringTable {
  readonly resolution: number;
  /** RGB triples: rows are altitude, columns are sun zenith cosine. */
  readonly data: Float32Array;

  private readonly params: AtmosphereParams;
  private readonly transmittance: TransmittanceTable;

  constructor(
    params: AtmosphereParams = EARTH_ATMOSPHERE,
    transmittance: TransmittanceTable = new TransmittanceTable(params),
    resolution = 32,
  ) {
    this.params = params;
    this.transmittance = transmittance;
    this.resolution = resolution;
    this.data = new Float32Array(resolution * resolution * 3);
    this.build();
  }

  private build(): void {
    const { params, resolution } = this;
    const thicknessM = params.topRadiusM - params.groundRadiusM;

    for (let y = 0; y < resolution; y++) {
      const v = y / (resolution - 1);
      const altitude = v * v * thicknessM;

      for (let x = 0; x < resolution; x++) {
        const muSun = (x / (resolution - 1)) * 2 - 1;
        this.integrate(altitude, muSun, msScratchA);
        const index = (y * resolution + x) * 3;
        this.data[index] = msScratchA.r;
        this.data[index + 1] = msScratchA.g;
        this.data[index + 2] = msScratchA.b;
      }
    }
  }

  /**
   * Second-order scattering arriving at one (altitude, sun zenith), summed to infinite order.
   *
   * Eight sample directions distributed over the sphere. That is coarse, and it is fine: the
   * function being integrated is smooth and nearly isotropic by construction, and the result is
   * a low-frequency energy correction, not a detail the eye can resolve.
   */
  private integrate(altitudeM: number, muSun: number, out: Rgb): Rgb {
    const { params } = this;
    const r = params.groundRadiusM + altitudeM;
    const directions = 8;
    const steps = 20;

    // `L` accumulates second-order in-scattered radiance; `f` accumulates the fraction of light
    // scattered *out* that will come back — the ratio driving the geometric series.
    let lR = 0;
    let lG = 0;
    let lB = 0;
    let fR = 0;
    let fG = 0;
    let fB = 0;

    for (let d = 0; d < directions; d++) {
      // Uniform in cos(zenith), so every sample carries the same solid angle.
      const mu = ((d + 0.5) / directions) * 2 - 1;
      const distance = distanceToAtmosphereExit(params, r, mu);
      const dt = distance / steps;

      let opticalR = 0;
      let opticalG = 0;
      let opticalB = 0;

      // A zero-length ray — looking straight down while standing on the ground — has no volume
      // to integrate, but it still ends on a surface, and the boundary term below is exactly
      // where the light from that surface enters. Guarding the whole direction on `distance > 0`
      // silently dropped the ground bounce for every viewer at sea level, which is the one
      // altitude the sky is looked at from most.
      for (let i = 0; distance > 0 && i < steps; i++) {
        const t = (i + 0.5) * dt;
        const radius = Math.sqrt(t * t + 2 * r * mu * t + r * r);
        const height = radius - params.groundRadiusM;

        extinctionAt(params, height, msScratchB);
        // Transmittance from the eye to this sample, accumulated as we march.
        const stepR = Math.exp(-opticalR);
        const stepG = Math.exp(-opticalG);
        const stepB = Math.exp(-opticalB);
        opticalR += msScratchB.r * dt;
        opticalG += msScratchB.g * dt;
        opticalB += msScratchB.b * dt;

        // The sun's zenith cosine changes along the ray as the sample moves round the planet.
        // `muSun · mu` is the *azimuthal average* of the view-sun angle over the cone at this
        // zenith angle — exact for the sphere average being computed here, and the reason this
        // integral needs only zenith samples rather than a full spherical distribution.
        const muSunHere = Math.min(
          1,
          Math.max(-1, (r * muSun + t * muSun * mu) / Math.max(radius, 1e-6)),
        );
        this.transmittance.sample(height, muSunHere, msScratchA);

        const rayleigh = rayleighDensity(params, height);
        const mie = mieDensity(params, height);
        // Isotropic: the 1/4π is the phase function of a scatterer that has forgotten the sun.
        const isotropic = 1 / (4 * Math.PI);
        const sR = (params.rayleighScattering.r * rayleigh + params.mieScattering * mie) * isotropic;
        const sG = (params.rayleighScattering.g * rayleigh + params.mieScattering * mie) * isotropic;
        const sB = (params.rayleighScattering.b * rayleigh + params.mieScattering * mie) * isotropic;

        lR += stepR * sR * msScratchA.r * dt;
        lG += stepG * sG * msScratchA.g * dt;
        lB += stepB * sB * msScratchA.b * dt;

        fR += stepR * sR * dt;
        fG += stepG * sG * dt;
        fB += stepB * sB * dt;
      }

      /*
       * The ground bounce.
       *
       * Not an embellishment — without it, a viewer at sea level has half of their sphere
       * contributing nothing at all, and the table comes out *dimmer* at the surface than at
       * 40 km, which is backwards. It is also the term that makes bright ground brighten the
       * sky above it, which is why the air over snow or desert reads so differently from the
       * air over ocean. A boundary term, so no `dt`: it is a radiance arriving from a surface,
       * not an emission distributed along a path.
       */
      if (hitsGround(params, r, mu)) {
        const groundRadius = params.groundRadiusM;
        const muSunGround = Math.min(
          1,
          Math.max(-1, (r * muSun + distance * muSun * mu) / groundRadius),
        );
        if (muSunGround > 0) {
          this.transmittance.sample(0, muSunGround, msScratchA);
          // Lambertian: albedo/π, times the cosine of the sun on the surface.
          const lambert = muSunGround / Math.PI;
          const toGroundR = Math.exp(-opticalR);
          const toGroundG = Math.exp(-opticalG);
          const toGroundB = Math.exp(-opticalB);
          lR += toGroundR * params.groundAlbedo.r * lambert * msScratchA.r;
          lG += toGroundG * params.groundAlbedo.g * lambert * msScratchA.g;
          lB += toGroundB * params.groundAlbedo.b * lambert * msScratchA.b;
        }
      }
    }

    const norm = 1 / directions;
    lR *= norm;
    lG *= norm;
    lB *= norm;
    fR *= norm;
    fG *= norm;
    fB *= norm;

    // 1/(1 − f) sums every further order. `f` is a scattering albedo and so is physically < 1;
    // clamp anyway, because a numerically unlucky bin turning the series negative would show up
    // as a black pixel in the sky and be extremely hard to trace back to here.
    return setRgb(
      out,
      lR / Math.max(1e-6, 1 - Math.min(0.98, fR)),
      lG / Math.max(1e-6, 1 - Math.min(0.98, fG)),
      lB / Math.max(1e-6, 1 - Math.min(0.98, fB)),
    );
  }

  /** Bilinear lookup on (altitude, sun zenith cosine). */
  sample(heightM: number, muSun: number, out: Rgb): Rgb {
    const thicknessM = this.params.topRadiusM - this.params.groundRadiusM;
    const altitude = Math.min(Math.max(heightM, 0), thicknessM);
    const v = Math.sqrt(altitude / thicknessM) * (this.resolution - 1);
    const u = ((Math.min(1, Math.max(-1, muSun)) + 1) / 2) * (this.resolution - 1);

    const x0 = Math.max(0, Math.min(this.resolution - 1, Math.floor(u)));
    const x1 = Math.min(this.resolution - 1, x0 + 1);
    const y0 = Math.max(0, Math.min(this.resolution - 1, Math.floor(v)));
    const y1 = Math.min(this.resolution - 1, y0 + 1);
    const fx = u - x0;
    const fy = v - y0;

    const i00 = (y0 * this.resolution + x0) * 3;
    const i10 = (y0 * this.resolution + x1) * 3;
    const i01 = (y1 * this.resolution + x0) * 3;
    const i11 = (y1 * this.resolution + x1) * 3;

    out.r = lerp2(this.data[i00], this.data[i10], this.data[i01], this.data[i11], fx, fy);
    out.g = lerp2(
      this.data[i00 + 1],
      this.data[i10 + 1],
      this.data[i01 + 1],
      this.data[i11 + 1],
      fx,
      fy,
    );
    out.b = lerp2(
      this.data[i00 + 2],
      this.data[i10 + 2],
      this.data[i01 + 2],
      this.data[i11 + 2],
      fx,
      fy,
    );
    return out;
  }
}

function lerp2(v00: number, v10: number, v01: number, v11: number, fx: number, fy: number): number {
  return (v00 * (1 - fx) + v10 * fx) * (1 - fy) + (v01 * (1 - fx) + v11 * fx) * fy;
}

// --- the scattering integral -------------------------------------------------------------------

const siExtinction = /* #__PURE__ */ rgb();
const siSunTransmittance = /* #__PURE__ */ rgb();
const siMulti = /* #__PURE__ */ rgb();

export interface SkyRadianceOptions {
  params?: AtmosphereParams;
  transmittance: TransmittanceTable;
  multipleScattering: MultipleScatteringTable;
  /** Raymarch steps. 32 is indistinguishable from 512 at the LUT's resolution. */
  steps?: number;
}

/**
 * Radiance arriving from one direction, for a viewer at `heightM`.
 *
 * The geometry is expressed entirely in two cosines and one dot product, because a spherically
 * symmetric atmosphere has no other degrees of freedom:
 *
 * @param viewMu    cosine of the angle between the view ray and local up.
 * @param sunMu     cosine of the angle between the sun and local up (its elevation, effectively).
 * @param cosTheta  cosine of the angle between the view ray and the sun — what the phase
 *                  functions are a function of, and the reason the sky brightens toward the sun.
 */
export function skyRadiance(
  heightM: number,
  viewMu: number,
  sunMu: number,
  cosTheta: number,
  options: SkyRadianceOptions,
  out: Rgb,
): Rgb {
  const params = options.params ?? EARTH_ATMOSPHERE;
  const steps = options.steps ?? 32;
  const r = params.groundRadiusM + Math.max(0, heightM);

  const distance = distanceToAtmosphereExit(params, r, viewMu);
  if (!(distance > 0)) return setRgb(out, 0, 0, 0);

  const phaseR = rayleighPhase(cosTheta);
  const phaseM = miePhase(cosTheta, params.mieAnisotropy);

  const dt = distance / steps;
  let opticalR = 0;
  let opticalG = 0;
  let opticalB = 0;
  let accR = 0;
  let accG = 0;
  let accB = 0;

  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) * dt;
    const radius = Math.sqrt(t * t + 2 * r * viewMu * t + r * r);
    const height = radius - params.groundRadiusM;

    extinctionAt(params, height, siExtinction);
    // Transmittance from the eye to this sample. Taken *before* adding this step's optical
    // depth, so a sample is lit by the air in front of it and not by itself.
    const transmittanceR = Math.exp(-opticalR);
    const transmittanceG = Math.exp(-opticalG);
    const transmittanceB = Math.exp(-opticalB);
    opticalR += siExtinction.r * dt;
    opticalG += siExtinction.g * dt;
    opticalB += siExtinction.b * dt;

    // How high the sun is *as seen from this sample*. Projecting the sun direction onto the
    // sample's own local zenith is what makes the terminator curve correctly — at sunset the
    // air above you is still in sunlight while the air at your altitude is not, and that is the
    // whole reason the sky stays lit for half an hour after the sun goes down.
    const sunMuHere = Math.min(
      1,
      Math.max(-1, (r * sunMu + t * cosTheta) / Math.max(radius, 1e-6)),
    );
    options.transmittance.sample(height, sunMuHere, siSunTransmittance);
    options.multipleScattering.sample(height, sunMuHere, siMulti);

    const densityR = rayleighDensity(params, height);
    const densityM = mieDensity(params, height);
    const sRayR = params.rayleighScattering.r * densityR;
    const sRayG = params.rayleighScattering.g * densityR;
    const sRayB = params.rayleighScattering.b * densityR;
    const sMie = params.mieScattering * densityM;

    // Single scattering: sunlight that survived the trip in, redirected toward the eye by the
    // phase function. Plus the isotropic multiple-scattering energy, which has no phase term
    // because by then the light no longer knows where the sun was.
    const inR = (sRayR * phaseR + sMie * phaseM) * siSunTransmittance.r + (sRayR + sMie) * siMulti.r;
    const inG = (sRayG * phaseR + sMie * phaseM) * siSunTransmittance.g + (sRayG + sMie) * siMulti.g;
    const inB = (sRayB * phaseR + sMie * phaseM) * siSunTransmittance.b + (sRayB + sMie) * siMulti.b;

    accR += transmittanceR * inR * dt;
    accG += transmittanceG * inG * dt;
    accB += transmittanceB * inB * dt;
  }

  return setRgb(out, accR, accG, accB);
}

/**
 * The sun's own colour and intensity as seen from `heightM`, relative to its colour in space.
 *
 * This is just transmittance along the sun ray, and it is why the disc goes from white overhead
 * to deep orange on the horizon: the blue end has been scattered out of the beam on the way
 * down, and at a 10° elevation the beam has travelled through five times as much air as it does
 * at noon.
 */
export function sunTransmittance(
  transmittance: TransmittanceTable,
  heightM: number,
  sunMu: number,
  out: Rgb,
): Rgb {
  return transmittance.sample(Math.max(0, heightM), sunMu, out);
}
